/**
 * Waiting on you's behavior half: the dashboard's `waitingOnYou` as the server orders it (held
 * bookings, the sent forms, approved requests still to pay; web-account-pages D12), and the
 * unread messages, the header's count once the tab's chat reads (so they agree, Telegram arrivals
 * included; Gabriel 2026-10-23); whether the forms summary is open. Draws nothing. The server owns
 * the kinds, so no booking appears twice.
 */
import { useMemo, useState } from 'react';
import { formatFinishByDay } from '@/utils/waitingBooking';

import { useSiteStudio } from '../../api/site';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { useWebDashboard } from '../../hooks/useWebAccount';
import { useMessagesPanel } from '../../messages/panelContext';
import { useWebChat } from '../../messages/WebChat';
import { usePageMover } from '../../pageMover';
import { contract, type WaitingOnYouActions, type WaitingOnYouEvents, type WaitingOnYouState } from './contract';

export type WaitingOnYouRuntime = PieceRuntime<typeof contract, WaitingOnYouState, WaitingOnYouActions>;

/** From this many forms on, they fold into one summary (Gabriel 2026-10-22). */
const SUMMARY_FROM = 3;

const SENT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const DATE = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const bookingPath = (id: string) => `/account/bookings/${encodeURIComponent(id)}`;

export function useWaitingOnYou(input: PieceInput<WaitingOnYouEvents> = {}): WaitingOnYouRuntime {
  const options = usePieceOptions(contract, input.options);
  const d = useWebDashboard().data;
  const studioName = useSiteStudio().data?.name ?? '';
  const { openMessages } = useMessagesPanel();
  const chat = useWebChat().state;
  const navigate = usePageMover();
  const [formsOpen, setFormsOpen] = useState(false);

  const { held, all, payments } = useMemo(() => {
    const items = d?.waitingOnYou ?? [];
    return {
      held: items.flatMap((i) =>
        i.kind === 'held'
          ? [{
              name: i.offeringName,
              date: DATE.format(new Date(i.scheduledAt)),
              finishBy: keepTimes(formatFinishByDay(i.completeBy)),
              action: i.label,
              href: `${bookingPath(i.bookingId)}/${i.target}`,
            }]
          : [],
      ),
      all: items.flatMap((i) =>
        i.kind === 'form'
          ? [{ name: i.formName, sent: `sent ${SENT.format(new Date(i.sentAt))}`, href: `/form-request/${encodeURIComponent(i.requestToken)}` }]
          : [],
      ),
      payments: items.flatMap((i) => (i.kind === 'approvedPayment' ? [{ name: i.offeringName, href: `${bookingPath(i.bookingId)}/pay` }] : [])),
    };
  }, [d?.waitingOnYou]);

  const folded = all.length >= SUMMARY_FROM;
  const unread = chat.live ? chat.unread : (d?.chatUnreadSecure ?? 0);
  const actions = useMemo<WaitingOnYouActions>(
    () => ({
      openForms: () => setFormsOpen(true),
      closeForms: () => setFormsOpen(false),
      readMessages: () => openMessages(),
      open: (href) => navigate(href),
    }),
    [openMessages, navigate],
  );

  return {
    state: {
      held,
      forms: folded && !formsOpen ? [] : all,
      summary: folded ? { count: all.length, oldestSent: `oldest ${all[0]!.sent}` } : null,
      formsOpen: folded && formsOpen,
      payments,
      messages: unread > 0 ? `${unread} new ${unread === 1 ? 'message' : 'messages'}` : null,
      studioName,
      empty: held.length === 0 && all.length === 0 && payments.length === 0 && unread === 0,
    },
    actions,
    options,
  };
}
